import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { capturePrivateSession, isPrivateSessionCurrent } from "@/lib/api/private-session";
import {
  firebaseNotificationFeedAdapter,
  type NotificationFeedAdapter,
  type RestaurantNotification,
} from "@/lib/firebase/notification-feed";
import type { Timestamp } from "firebase/firestore";
import { isRestaurantPersistentNotificationType } from "@/lib/firebase/notification-types";

type FeedState = {
  scopeKey: string;
  notifications: RestaurantNotification[];
  error: unknown;
};

export function useNotificationFeed({
  ownerId,
  storeId,
  enabled,
  adapter = firebaseNotificationFeedAdapter,
  onLiveItems,
}: {
  ownerId: string | undefined;
  storeId: string | undefined;
  enabled: boolean;
  adapter?: NotificationFeedAdapter;
  onLiveItems?: (items: RestaurantNotification[]) => void;
}) {
  const onLiveItemsRef = useRef(onLiveItems);
  useEffect(() => {
    onLiveItemsRef.current = onLiveItems;
  }, [onLiveItems]);
  const scopeKey = enabled && ownerId && storeId ? `${ownerId}:${storeId}` : "";
  const [lifetime] = useState(capturePrivateSession);
  // A return to the same owner/store gets a new token; only a committed,
  // mounted scope can act, even before passive subscription cleanup runs.
  const scope = useMemo(() => ({ scopeKey, adapter }), [scopeKey, adapter]);
  const committedScopeRef = useRef<typeof scope | null>(null);
  useLayoutEffect(() => {
    committedScopeRef.current = scope;
    return () => {
      committedScopeRef.current = null;
    };
  }, [scope]);
  const isCurrent = useCallback(
    () => committedScopeRef.current === scope && isPrivateSessionCurrent(lifetime),
    [lifetime, scope],
  );
  const [state, setState] = useState<FeedState>({
    scopeKey: "",
    notifications: [],
    error: null,
  });
  const [retryVersion, setRetryVersion] = useState(0);

  // Never expose the previous store's rows during the render before the new
  // listener settles. The state keeps its own scope and only matches are read.
  const notifications = useMemo(
    () => (state.scopeKey === scopeKey ? state.notifications : []),
    [scopeKey, state.notifications, state.scopeKey],
  );
  const error = state.scopeKey === scopeKey ? state.error : null;
  useEffect(() => {
    if (!scopeKey || !ownerId || !storeId || !isCurrent()) return;
    let active = true;
    let baselineReady = false;
    let historyCutoff: Timestamp | null = null;
    const history = new Set<string>();
    const seen = new Set<string>();
    const unsubscribe = adapter.subscribe(
      { ownerId, storeId },
      (nextNotifications, metadata) => {
        if (active && isCurrent()) {
          const visible = nextNotifications.filter((item) =>
            isRestaurantPersistentNotificationType(item.type),
          );
          setState({
            scopeKey,
            notifications: visible,
            error: null,
          });
          // Cached/pending views can render but never consume a live identity.
          if (metadata?.fromCache || metadata?.hasPendingWrites) return;
          if (!baselineReady || metadata?.isBaseline) {
            const snapshotCutoff = metadata?.latestCreatedAt;
            if (snapshotCutoff && Number.isFinite(snapshotCutoff.toMillis()) &&
              (!historyCutoff || snapshotCutoff.seconds > historyCutoff.seconds ||
                (snapshotCutoff.seconds === historyCutoff.seconds && snapshotCutoff.nanoseconds > historyCutoff.nanoseconds))) {
              historyCutoff = snapshotCutoff;
            }
            for (const item of visible) {
              history.add(item.id);
              const createdAt = item.createdAt;
              if (createdAt && Number.isFinite(createdAt.toMillis()) &&
                (!historyCutoff || createdAt.seconds > historyCutoff.seconds ||
                  (createdAt.seconds === historyCutoff.seconds && createdAt.nanoseconds > historyCutoff.nanoseconds))) {
                historyCutoff = createdAt;
              }
            }
            baselineReady = true;
            return;
          }
          const added: RestaurantNotification[] = [];
          for (const item of visible) {
            if (history.has(item.id)) continue;
            const createdAt = item.createdAt;
            if (!createdAt || !Number.isFinite(createdAt.toMillis())) continue;
            const fresh = !seen.has(item.id);
            seen.add(item.id);
            if (seen.size > 200) seen.delete(seen.values().next().value!);
            if (fresh && !item.read &&
              (!historyCutoff || createdAt.seconds > historyCutoff.seconds ||
                (createdAt.seconds === historyCutoff.seconds && createdAt.nanoseconds > historyCutoff.nanoseconds))) {
              added.push(item);
            }
          }
          if (added.length > 0) onLiveItemsRef.current?.(added);
        }
      },
      (nextError) => {
        if (active && isCurrent()) {
          console.warn(
            "[restaurant-notifications] Firestore feed listener failed",
            nextError,
          );
          setState({ scopeKey, notifications: [], error: nextError });
        }
      },
    );
    return () => {
      active = false;
      unsubscribe();
    };
  }, [adapter, isCurrent, ownerId, retryVersion, scopeKey, storeId]);

  const retryFeed = useCallback(() => {
    if (!scopeKey || !isCurrent()) return;
    setState((current) =>
      current.scopeKey === scopeKey ? { ...current, error: null } : current,
    );
    setRetryVersion((version) => version + 1);
  }, [isCurrent, scopeKey]);

  const markAsRead = useCallback(
    async (notificationId: string) => {
      if (!isCurrent() || !notifications.some((item) => item.id === notificationId)) {
        return;
      }
      try {
        await adapter.markRead(notificationId);
        if (!isCurrent()) return;
        setState((current) =>
          current.scopeKey !== scopeKey
            ? current
            : {
                ...current,
                notifications: current.notifications.map((item) =>
                  item.id === notificationId ? { ...item, read: true } : item,
                ),
              },
        );
      } catch (failure) {
        console.warn(
          "[restaurant-notifications] Failed to mark notification read",
          failure,
        );
      }
    },
    [adapter, isCurrent, notifications, scopeKey],
  );

  const markAllAsRead = useCallback(async () => {
    if (!isCurrent()) return;
    const unreadIds = notifications
      .filter((item) => !item.read)
      .map((item) => item.id);
    if (unreadIds.length === 0) return;
    try {
      await adapter.markAllRead(unreadIds);
      if (!isCurrent()) return;
      const unreadSet = new Set(unreadIds);
      setState((current) =>
        current.scopeKey !== scopeKey
          ? current
          : {
              ...current,
              notifications: current.notifications.map((item) =>
                unreadSet.has(item.id) ? { ...item, read: true } : item,
              ),
            },
      );
    } catch (failure) {
      console.warn(
        "[restaurant-notifications] Failed to mark all notifications read",
        failure,
      );
    }
  }, [adapter, isCurrent, notifications, scopeKey]);

  return {
    notifications,
    unreadCount: notifications.filter((item) => !item.read).length,
    error,
    retryFeed,
    markAsRead,
    markAllAsRead,
  };
}
